import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { sound } from "@pixi/sound";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { PixiPlugin } from "gsap/PixiPlugin";
import * as PIXI from "pixi.js";
import { BitmapText, Container } from "pixi.js";
import { Tween } from "tweedle.js";
import { isSuperQuickBtnOnGlobal } from "../../config/GlobalVariableConfig";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import { Manager } from "../../core/Manager";
// import { wait } from "../../core/utils";
import { SoundConstants } from "../../gameClientEvents/SoundConstants";
import { IFeatureData } from "../../server/getServerResposesFun";

gsap.registerPlugin(PixiPlugin, MotionPathPlugin);
PixiPlugin.registerPIXI(PIXI);

export class WinMeterContainer extends DisplayObject {
    private multiplierTextInstances: BitmapText[] = [];
    private labelName: string = "multiplier_";
    private symbolList: Spine[] = [];
    private centerOffset: number = 110;
    private multiplieryOffset: number = 10;
    public collectMeter: Spine;
    private collectedAmount: number = 0;

    private isFirstDoubleInRound: boolean = true;


    constructor(config: IDisplayObject) {
        super(config);
        this.setupCollectMeter();
    }

    private setupCollectMeter() {
        this.collectMeter = Spine.from({
            skeleton: "collectMeterSkel",
            atlas: "collectMeterAtlas"
        });
        this.collectMeter.label = "collect_meter";
        this.collectMeter.alpha = 0;
        this.addChild(this.collectMeter);
        this.collectMeter.state.setAnimation(0, "idle", true);

        const multText = new BitmapText({
            text: "",
            style: {
                fontFamily: 'skull_coin_number',
                fontSize: 105
            }
        });
        multText.label = "collect_meter_text";
        multText.anchor = 0.5;
        this.collectMeter.addSlotObject("multiplier", multText);
        this.setPositionResize(Manager.Orientation || "LANDSCAPE");
    }

    public hideCollectMeter() {
        sound.play(SoundConstants.LEFT_TOMBSTONE_VANISH, { volume: 1 });
        new Tween(this.collectMeter)
            .onComplete(
                () => this.collectMeter.state.setAnimation(0, "out", false)
            ).to({ alpha: 0 }, 100)
            .start();
    }

    public showCollectMeter() {
        sound.play(SoundConstants.LEFT_TOMBSTONE_APPEAR,{ volume: 1});
        new Tween(this.collectMeter)
            .to({ alpha: 1 }, 100)
            .onComplete(
                () => this.collectMeter.state.setAnimation(0, "in", false)
            )
            .start();
    }

    private collectAnim(val: number) {
        this.collectMeter.state.setAnimation(0, "collect", false);
        this.collectMeter.state.addAnimation(0, "idle", true, 0);
        this.collectedAmount += val;
        this.updateMultiplier(this.collectedAmount, this.collectMeter);
    }

    public setupMeter(label: string = "0", multiplier: number = 1) {
        this.symbolList = [];
        this.multiplierTextInstances = [];
        const multVal = multiplier;
        const multText = new BitmapText({
            text: `${multVal}x`,
            style: {
                fontFamily: 'skull_coin_number',
                fontSize: 160
            }
        });
        multText.label = this.labelName + label;
        multText.anchor = 0.5;
        multText.visible = false;
        this.multiplierTextInstances.push(multText);
        multText.scale = 0;
        this.addChild(multText);
    }

    public revealMultiplier(id: string, multiplier: number, symbol: Spine) {
        sound.play(SoundConstants.COIN_FLIP,{volume: 0.5});
        let multiplierInst = this.getChildByLabel(this.labelName + id) as BitmapText;
        multiplierInst.style.fontSize = 180;
        multiplierInst.text = `${multiplier}x`;
        multiplierInst.scale = 1;
        multiplierInst.visible = true;
        (symbol) && (symbol.addSlotObject("multiplier", multiplierInst));
        this.symbolList.push(symbol);
    }

    public updateMultiplier(multiplier: number, symbol: Spine) {
        let multiplierInst = symbol.getSlotObject("multiplier") as BitmapText;
        if (multiplierInst) {
            multiplierInst.text = `${multiplier}x`;
        }
    }

    public addMultiplierTravelAnimation_Add(multText: BitmapText, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        fly.position.set(start.x, start.y);

        const wave = { t: 0 };

        const amplitude = 22;
        const frequency = 3;
        const lift = 60;

        gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                gsap.fromTo(
                    target.scale,
                    { x: 1, y: 1 },
                    { x: 1.1, y: 1.1, duration: 0.18, yoyo: true, repeat: 1, ease: "sine.out" }
                );
            }
        })
            .to(wave, {
                t: 1,
                duration: 0.85,
                ease: "sine.inOut",
                onUpdate: () => {
                    const waveX = Math.sin(wave.t * Math.PI * frequency) * amplitude;
                    const liftY = Math.sin(wave.t * Math.PI) * lift;

                    fly.x = start.x + (end.x - start.x) * wave.t + waveX;
                    fly.y = start.y + (end.y - start.y) * wave.t - liftY;

                    fly.rotation = Math.sin(wave.t * Math.PI) * 0.15;
                    fly.scale.set(1 - wave.t * 0.2);
                }
            });
    }

    public reinfestMultiplierTravelAnimation(src: Spine, mult: number, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: mult.toString(), style: target.style });
        fly.anchor.set(0.5);
        this.addChild(fly);
        fly.alpha = 0;

        const start = this.toLocal(src.getGlobalPosition());
        const end = this.toLocal(target.getGlobalPosition());

        gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();
            }
        })
            .set(fly, { x: start.x, y: start.y, alpha: 0, scale: 0.5 })
            .to(fly, { alpha: 1, scale: 1.2, duration: 0.2 })
            .to(fly, {
                duration: 0.8,
                ease: "power1.inOut",
                motionPath: {
                    path: [
                        { x: start.x, y: start.y },
                        { x: (start.x + end.x) / 2, y: Math.min(start.y, end.y) - 150 },
                        { x: end.x, y: end.y }
                    ],
                }
            })
            .to(fly, { scale: 0.5, alpha: 0, duration: 0.2 }, "-=0.2");

    }

    public reinfestMultiplierAnimation(mult: number, target: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: `+${mult}`, style: target.style });
        fly.anchor.set(0.5);
        this.addChild(fly);
        const end = this.toLocal(target.getGlobalPosition());
        fly.position.set(end.x, end.y);
        fly.alpha = 0;
        fly.scale.set(0.2);

        gsap.timeline({
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();
            }
        })

            .to(fly, {
                alpha: 1,
                scale: 0.5,
                duration: 0.15,
                ease: "rough({ template: power1.in, strength: 2, points: 10, taper: 'none', randomize: true, clamp:  true})"
            })

            .to(fly, {
                x: `+=${Math.random() * 10 - 5}`,
                y: `+=${Math.random() * 10 - 5}`,
                duration: 0.05,
                repeat: 10,
                yoyo: true
            })

            .to(fly, { y: end.y - 20, alpha: 0, duration: 0.5, ease: "power2.in" });

    }

    public playReinfestTrail(
        trailContainer: Container,
        trailAnim: string,
        multiplier: number,
        targetSymbol?: Spine,
        styleSource?: BitmapText,
        timeScale: number = 1
    ): Promise<void> {
        return new Promise<void>((resolve) => {
            const trail = Spine.from({
                skeleton: "featureTrailSkel",
                atlas: "featureTrailAtlas"
            });

            if (!trail.skeleton.data.findAnimation(trailAnim)) {
                trail.destroy();
                resolve();
                return;
            }

            const skull_effect = Spine.from({
                skeleton: "featureSkullImpactSkel",
                atlas: "featureSkullImpactAtlas"
            });
            skull_effect.label = "skull_effect";
            skull_effect.visible = false;

            const trailText = new BitmapText({
                text: `${multiplier}x`,
                style: styleSource?.style ?? {
                    fontFamily: "skull_coin_number",
                    fontSize: 160
                }
            });
            trailText.anchor = 0.5;

            const slot = trail.skeleton.findSlot("multiplier");
            if (slot) {
                trail.addSlotObject("multiplier", trailText);
            }

            trailContainer.addChild(trail);
            trailContainer.addChild(skull_effect);

            let multiplierText: BitmapText;

            const playSkullEffect = () => {
                if (!targetSymbol) {
                    trailContainer.removeChild(skull_effect);
                    skull_effect.destroy();
                    return;
                }

                const targetPos = trailContainer.toLocal(targetSymbol.getGlobalPosition());
                skull_effect.position.set(targetPos.x, targetPos.y);
                skull_effect.visible = true;
                skull_effect.state.timeScale = timeScale;
                skull_effect.state.setAnimation(0, "skull_impact", false).listener = {
                    complete: () => {
                        skull_effect.state.clearListeners();
                        trailContainer.removeChild(skull_effect);
                        skull_effect.destroy();
                    }
                };
            };

            const listener = {
                complete: async () => {
                    trail.state.removeListener(listener);

                    playSkullEffect();

                    if (targetSymbol?.skeleton?.data?.findAnimation("multiplier_impact")) {
                        targetSymbol.state.timeScale = timeScale;
                        targetSymbol.state.setAnimation(0, "multiplier_impact", false);

                        // targetSymbol.removeSlotObjects();
                        try {
                            targetSymbol.removeSlotObject("multiplier2");
                        }
                        catch (_) {
                        }
                        targetSymbol.addSlotObject("multiplier2", multiplierText);

                        if (targetSymbol.skeleton.data.findAnimation("multiplier_idle")) {
                            targetSymbol.state.addAnimation(0, "multiplier_idle", true, 0);
                        }
                    }

                    if (slot) {
                        trail.removeSlotObjects();
                    }
                    trailContainer.removeChild(trail);
                    trail.destroy();
                    resolve();
                }
            };

            multiplierText = new BitmapText({
                text: "",
                style: {
                    fontFamily: "skull_coin_number",
                    fontSize: 100,
                    letterSpacing: 5,
                }
            });
            multiplierText.text = `X${multiplier}`;
            multiplierText.anchor = 0.5;
            multiplierText.alpha = 1;
            multiplierText.visible = true;

            trail.state.addListener(listener);
            trail.state.timeScale = timeScale;
            trail.state.setAnimation(0, trailAnim, false);
            sound.play(SoundConstants.TRAIL,{ volume: 1 });
        });
    }


    public addMultiplierTravelAnimation_Collect(multText: BitmapText, callback?: () => void) {
        const fly = new BitmapText({ text: multText.text, style: multText.style });
        fly.anchor.set(0.5);
        fly.visible = false;
        this.addChild(fly);

        const start = this.toLocal(multText.getGlobalPosition());
        const end = this.toLocal(this.collectMeter.getGlobalPosition());
        end.y += this.multiplieryOffset;

        const waveAmplitude = 40;
        const waveFrequency = 1.5;


        const animationState = { p: 0 };

        gsap.to(animationState, {
            p: 1,
            duration: 0.8,
            ease: "sine.inOut",
            onUpdate: () => {
                fly.visible = true;
                const p = animationState.p;


                const curX = start.x + (end.x - start.x) * p;
                const curY = start.y + (end.y - start.y) * p;


                const wave = Math.sin(p * Math.PI * waveFrequency) * waveAmplitude;

                fly.x = curX;
                fly.y = curY + wave;


                const s = 1 - (0.25 * p);
                fly.scale.set(s);
            },
            onComplete: () => {
                this.removeChild(fly);
                fly.destroy();
                callback?.();

                this.collectAnim(parseInt(multText.text));
            }
        });
    }

    public reawakenMorph(textObj: BitmapText, newValue: number) {
        const tl = gsap.timeline();
        sound.play(SoundConstants.REAWAKEN_SYMBOL,{ volume: 1 });
        tl.to(textObj.scale, {
            x: 0,
            y: 0,
            duration: 0.2
        })
            .call(() => {
                textObj.text = newValue.toString() + "x";
            })
            .to(textObj.scale, {
                x: 0.7,
                y: 0.7,
                duration: 0.4,
                ease: "back.inOut(2)"
            })
            .to(textObj.scale, {
                x: 0.55,
                y: 0.55,
                duration: 0.4,
                ease: "power2.in"
            });


        return tl;
    }

    public doubleMultiplierAnimation(textObj: BitmapText, newValue: number, callback?: () => void) {

        const start = this.toLocal(textObj.getGlobalPosition());

        const left = new BitmapText({ text: textObj.text, style: textObj.style });
        const right = new BitmapText({ text: textObj.text, style: textObj.style });

        left.anchor.set(0.5);
        right.anchor.set(0.5);

        left.visible = false;
        right.visible = false;

        this.addChild(left, right);

        left.position.set(start.x, start.y);
        right.position.set(start.x, start.y);

        const spread = 45;

        const anim = { p: 0 };

        textObj.visible = false;

        gsap.to(anim, {
            p: 1,
            duration: 0.7,
            ease: "sine.inOut",

            onUpdate: () => {

                left.visible = true;
                right.visible = true;

                const p = anim.p;

                if (p < 0.5) {

                    const t = p * 2;

                    left.x = start.x - spread * t;
                    right.x = start.x + spread * t;

                } else {

                    const t = (p - 0.5) * 2;

                    left.x = start.x - spread * (1 - t);
                    right.x = start.x + spread * (1 - t);

                    const s = 1 - (0.4 * t);

                    left.scale.set(s);
                    right.scale.set(s);

                    left.alpha = 1 - t;
                    right.alpha = 1 - t;
                }

                const lift = Math.sin(p * Math.PI) * 12;

                left.y = start.y - lift;
                right.y = start.y - lift;
            },

            onComplete: () => {

                this.removeChild(left, right);
                left.destroy();
                right.destroy();

                textObj.text = newValue.toString() + "x";
                textObj.visible = true;

                gsap.fromTo(textObj.scale,
                    { x: 1.8, y: 1.8 },
                    { x: 1, y: 1, duration: 0.25, ease: "back.out(2)" }
                );

                callback?.();
            }
        });
    }

    public reinfestMultiplySpiralAnimation(textObj: BitmapText, multiplier: number, newValue: number, callback?: () => void) {

        const start = this.toLocal(textObj.getGlobalPosition());

        const multText = new BitmapText({
            text: multiplier.toString() + "x",
            style: textObj.style
        });

        multText.anchor.set(0.5);
        multText.visible = false;

        this.addChild(multText);

        multText.position.set(start.x, start.y - 40);

        const anim = { p: 0 };

        const spiralRadius = 35;
        const rotations = 2;

        gsap.to(anim, {
            p: 1,
            duration: 0.8,
            ease: "power2.in",

            onUpdate: () => {

                multText.visible = true;

                const p = anim.p;

                const angle = rotations * Math.PI * 2 * p;
                const r = spiralRadius * (1 - p);

                multText.x = start.x + Math.cos(angle) * r;
                multText.y = start.y + Math.sin(angle) * r;

                const s = 1 - (0.6 * p);
                multText.scale.set(s);

                multText.alpha = 1 - (0.5 * p);
            },

            onComplete: () => {

                this.removeChild(multText);
                multText.destroy();

                textObj.text = newValue.toString() + "x";

                gsap.fromTo(textObj.scale,
                    { x: 1.8, y: 1.8 },
                    { x: 1, y: 1, duration: 0.25, ease: "back.out(2)" }
                );

                callback?.();
            }
        });
    }

    public async travelMultiplier(spineSymbol: Spine, targetMult: number): Promise<void> {
        await new Promise<void>((resolve) => {

            const listener = {
                event: async (_entry: any, event: any) => {
                    if (event.data.name === "reveal2") {
                        // await wait(300);
                        spineSymbol.removeSlotObject("multiplier2");
                        this.updateMultiplier(targetMult, spineSymbol);
                    }
                },
                complete: () => {
                    spineSymbol.state.removeListener(listener);
                    resolve();
                }
            };

            spineSymbol.state.addListener(listener);
            sound.play(SoundConstants.COIN_FLIP,{ volume: 0.4 });
            spineSymbol.state.setAnimation(0, "multiplier_hit", false);
        });
    }

    public collectAllMultipliersSpiral(texts: BitmapText[], target: Spine, callback?: () => void) {
        const duration = 0.9;
        const center = this.toLocal(target.getGlobalPosition());
        center.y -= this.centerOffset;

        texts.forEach((txt, i) => {
            const start = this.toLocal(txt.getGlobalPosition());
            const fly = new BitmapText({ text: txt.text, style: txt.style });
            fly.anchor.set(0.5);
            fly.position.set(start.x, start.y);
            this.addChild(fly);

            const angleOffset = i * 0.6;
            const radiusStart = 160 + i * 10;
            const proxy = { angle: angleOffset, radius: radiusStart };
            if (txt.label === "accumWinText") {
                txt.visible = false;
            }

            gsap.to(proxy, {
                angle: angleOffset + Math.PI * 2,
                radius: 0,
                duration,
                ease: "sine.inOut",
                onUpdate: () => {
                    fly.x = center.x + Math.cos(proxy.angle) * proxy.radius;
                    fly.y = center.y + Math.sin(proxy.angle) * proxy.radius;
                    fly.scale.set(0.6 + (1 - proxy.radius / radiusStart) * 0.4);
                    fly.alpha = proxy.radius / radiusStart;
                },
                onComplete: () => {
                    this.removeChild(fly);
                    fly.destroy();

                    if (i === texts.length - 1) {
                        callback?.();
                    }
                }
            });
        });
    }

    public showTotalWinMeter(target: Spine, amount: number, callback?: () => void) {
        const center = this.toLocal(target.getGlobalPosition());
        center.y -= this.centerOffset;

        const totalText = new BitmapText({
            text: `${amount}`, style: {
                fontFamily: 'skull_coin_number',
                fontSize: 160,
            }
        });

        totalText.anchor.set(0.5);
        totalText.visible = false;
        totalText.position = center;
        //this.effectAnim.position = center;

        this.addChild(totalText);
        totalText.alpha = 0;

        const tl = gsap.timeline();

        tl.to(totalText, {
            alpha: 1,
            onStart: () => {
                totalText.visible = true;
                // this.effectAnim.visible = true;
                // this.effectAnim.state.setAnimation(0, "in", false);
                // this.effectAnim.state.addAnimation(0, "loop", true, 0);
            }
        })
            .to({}, { duration: 2 })
            .to(totalText, {
                alpha: 0,
                duration: 1,
                onStart: () => {
                    // this.effectAnim.state.setAnimation(0, "out", false);
                },
                onComplete: () => {
                    this.removeChild(totalText);
                    totalText.destroy();

                    // this.effectAnim.visible = false;
                    // this.effectAnim.state.clearTracks();

                    callback?.();
                }
            });
    }

    public resetMultipliers() {
        this.symbolList && this.symbolList.forEach((symbol) => {
            if (!symbol.destroyed) {
                let obj = symbol?.getSlotObject("multiplier");
                obj?.destroy();
                (obj) && (symbol.removeSlotObject("multiplier"));
            }
        });
        this.symbolList = [];
        const collectText = this.collectMeter.getSlotObject("multiplier") as BitmapText;
        collectText.text = "";
        this.collectedAmount = 0;
        this.isFirstDoubleInRound = true;
    }

    public override destroy() {
        super.destroy();
        this.symbolList = [];
        this.multiplierTextInstances = [];
    }

    public override setPositionResize(orientation: string) {
        super.setPositionResize(orientation);
        if (orientation === "LANDSCAPE") {
            this.collectMeter?.position.set(-250, 500);
        } else {
            this.collectMeter?.position.set(375, -290);
        }
    }

    private getTriggeredFeatures(featureData: IFeatureData): string[] {
        if (!featureData?.feature?.data) return [];

        return featureData.feature.data.map((feature) => {
            switch (feature.name) {
                case "replicate":
                    return "double";
                case "reinfest":
                    return "multi";
                case "reawaken":
                    return "extra";
                default:
                    return null;
            }
        }).filter((feature) => feature !== null) as string[];
    }

    public resetFeatureAnimationState() {
        this.isFirstDoubleInRound = true;
    }

    private shouldPlayIntro(type: string, features: string[]): boolean {
        if (!features.includes(type)) {
            return false;
        }

        const isTripleCombination = features.includes("double") && features.includes("multi") && features.includes("extra");

        if (!isTripleCombination) {
            return false;
        }

        // For triple combination, skip the intro for "double" on its very first play
        // since it's already visible — playing "in" would cause a flicker
        if (type === "double" && this.isFirstDoubleInRound) {
            this.isFirstDoubleInRound = false;
            return false;
        }

        return true;
    }

    private getWinAnim(type: string, features: string[]): string {
        if (features.length === 1) {
            return "animation";
        }

        return type + "_win";
    }

    public playSkullFeatureAnimation(sym: Spine, featureData: IFeatureData, currentFeatureName: string): Promise<void> {
        const features = this.getTriggeredFeatures(featureData);
        const currentType = (() => {
            switch (currentFeatureName) {
                case "replicate":
                    return "double";
                case "reinfest":
                    return "multi";
                case "reawaken":
                    return "extra";
                default:
                    return null;
            }
        })();
        sound.play(SoundConstants.RIP_REVEAL,{volume: 1});

        if (!sym || !currentType || !features.includes(currentType)) {
            return Promise.resolve();
        }

        sym.state.timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
        // sym.state.clearTracks();

        let first = true;

        if (this.shouldPlayIntro(currentType, features)) {
            const introAnim = currentType + "_in";
            if (sym.skeleton.data.findAnimation(introAnim)) {
                sym.state.setAnimation(0, introAnim, false);
                first = false;
            }
        }
        
        const winAnim = this.getWinAnim(currentType, features);
        if (!sym.skeleton.data.findAnimation(winAnim)) {
            sym.state.timeScale = 1;
            return Promise.resolve();
        }
        
        if (first) {
            sym.state.setAnimation(0, winAnim, false);
        } else {
            sym.state.addAnimation(0, winAnim, false, 0);
        }

        return new Promise<void>((resolve) => {
            const currentTrack = sym.state.tracks[0];
            if (currentTrack) {
                currentTrack.listener = {
                    complete: () => {
                        sym.state.timeScale = 1;
                        resolve();
                    }
                };
            } else {
                sym.state.timeScale = 1;
                resolve();
            }
        });
    }
}